import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const ACTIVITY_KEY = "nexreq:last-activity";
const WARN_MS = 2 * 60 * 1000;
const EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];

function markActive() {
  localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
}

export function IdleTimeoutGuard() {
  const { user, logout } = useAuth();
  const [timeoutMs, setTimeoutMs] = useState(0);
  const [warning, setWarning] = useState(false);
  const throttle = useRef(0);

  useEffect(() => {
    if (!user) return;
    api.get("/auth/session-policy")
      .then(({ data }) => setTimeoutMs((data.idle_timeout_minutes || 0) * 60000))
      .catch(() => setTimeoutMs(0));
  }, [user]);

  useEffect(() => {
    if (!timeoutMs) return undefined;
    markActive();
    const onActivity = () => {
      if (Date.now() - throttle.current > 5000) {
        throttle.current = Date.now();
        markActive();
      }
    };
    EVENTS.forEach((name) => window.addEventListener(name, onActivity, { passive: true }));
    const timer = setInterval(async () => {
      const idle = Date.now() - Number(localStorage.getItem(ACTIVITY_KEY) || Date.now());
      if (idle >= timeoutMs) {
        try {
          const { data } = await api.get("/hrms/me/attendance");
          if (["working", "on_break"].includes(data?.status)) {
            markActive();
            setWarning(false);
            return;
          }
        } catch {}
        logout("idle_timeout");
      } else {
        setWarning(idle >= timeoutMs - WARN_MS);
      }
    }, 15000);
    return () => {
      EVENTS.forEach((name) => window.removeEventListener(name, onActivity));
      clearInterval(timer);
    };
  }, [timeoutMs, logout]);

  return (
    <Dialog open={warning} onOpenChange={(open) => !open && (markActive(), setWarning(false))}>
      <DialogContent data-testid="idle-timeout-warning-dialog">
        <DialogHeader>
          <DialogTitle>Still there?</DialogTitle>
          <DialogDescription>
            You will be signed out in about 2 minutes because of inactivity.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            onClick={() => { markActive(); setWarning(false); }}
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
            data-testid="idle-stay-signed-in-button"
          >
            Stay signed in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
